// src/api/apiFactory.jsx
import axios from "axios";
const getBackendOrigin = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // 1. FORCE HTTP for local development addresses to resolve protocol mismatches once and for all.
  // This covers localhost, 127.0.0.1, and local network IPs.
  const isLocal =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.") ||
    hostname.startsWith("172.");

  if (isLocal) {
    // Port 5005 is used to bypass the browser's HSTS cache on port 5000.
    return `http://${hostname}:5005`;
  }

  // 2. Production handling
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL.replace(/\/api\/v1\/?$/, "");

  return `${protocol}//${hostname}`;
};

let csrfToken = localStorage.getItem("csrf_token") || null;

export async function initializeCSRFToken(forceRefresh = false) {
  if (csrfToken && !forceRefresh) return csrfToken;
  try {
    const url = `${getBackendOrigin()}/csrf-token/`;
    const response = await axios.get(url, {
      withCredentials: true,
      headers: { Accept: "application/json" },
      timeout: 5000,
    });
    const token = response.data?.csrf_token || response.data?.token || null;
    if (!token) return null;
    csrfToken = token;
    localStorage.setItem("csrf_token", token);
    return token;
  } catch (error) {
    console.error("❌ CSRF fetch failed:", error.message);
    return null;
  }
}

/**
 * Factory to create isolated axios instance
 * @param {string} tokenKey - localStorage key for access token (e.g. 'access' or 'admin_token')
 * @param {string} redirectBase - base path for login redirects (e.g. '/auth/login' or '/admin/auth/login')
 */


export function createAPI(tokenKey, redirectBase = "/auth/login") {
  const baseOrigin = getBackendOrigin();
  const instance = axios.create({
    baseURL: `${baseOrigin}/api/v1`,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
  });

  // === Request Interceptor ===
  instance.interceptors.request.use(
    async (config) => {
      try {
        const token = localStorage.getItem(tokenKey);
        if (token) config.headers.Authorization = `Bearer ${token}`;

        if (!csrfToken) await initializeCSRFToken();
        if (csrfToken) config.headers["X-CSRFToken"] = csrfToken;

        if (config.data instanceof FormData) delete config.headers["Content-Type"];
      } catch (err) {
        console.warn("⚠️ Skipped token or CSRF attach:", err);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // === Response Interceptor ===
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;

      if (status === 400 && /csrf/i.test(error?.response?.data?.message || "")) {
        console.warn("⚠️ CSRF expired — refreshing...");
        await initializeCSRFToken(true);
        return instance.request(error.config);
      }

      if (status === 401) {
        const hasToken = !!localStorage.getItem(tokenKey);
        console.warn("⚠️ Unauthorized — redirecting to sign-in");
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(tokenKey === "admin_token" ? "admin" : "user");

        let redirectUrl = redirectBase;
        if (hasToken) {
          redirectUrl += (redirectUrl.includes("?") ? "&" : "?") + "expired=true";
        }

        try {
          const attemptedPath = `${window.location.pathname || ""}${window.location.search || ""}`;
          if (attemptedPath && attemptedPath !== "/" && attemptedPath !== redirectBase) {
            const encoded = encodeURIComponent(attemptedPath);
            redirectUrl += (redirectUrl.includes("?") ? "&" : "?") + `next=${encoded}`;
          }
          window.location.href = redirectUrl;
        } catch (e) {
          window.location.href = redirectUrl;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
}
